import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Container } from "reactstrap";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import StatsCards    from "./components/StatsCards";
import TemperatureChart from "./components/TemperatureChart";
import AlarmTable    from "./components/AlarmTable";
import PageGrid from "../../components/GridLayout/PageGrid";
import FieldCustomizerPanel, { useFieldCustomizer } from "../../components/FieldCustomizer/FieldCustomizer";
import "../../components/FieldCustomizer/FieldCustomizer.css";

const TEMP_FIELDS = [
  { value:"temperature", label:"Temperature" },
  { value:"time",        label:"Time"        },
];

const ALARM_COLS_DEFAULT = [
  { id:"time",        label:"Time",        field:"time",        visible:true },
  { id:"temperature", label:"Temperature", field:"temperature", visible:true },
  { id:"message",     label:"Message",     field:"message",     visible:true },
];

const TemperatureAlarm = () => {
  const [tempData,        setTempData]        = useState([]);
  const [alarmList,       setAlarmList]       = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [machines,        setMachines]        = useState([]);
  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedDate,    setSelectedDate]    = useState(new Date().toISOString().split("T")[0]);

  const alarmFC = useFieldCustomizer("temp_alarm_cols", [], ALARM_COLS_DEFAULT);

  /* ── API calls ───────────────────────────────────────────────── */
  const fetchMachines = async () => {
    try {
      const res = await axios.get("https://api.hanoch.in/api/machines/by-company/?id=6");
      const raw = res.data;
      const list = Array.isArray(raw) ? raw : Array.isArray(raw?.results) ? raw.results : Array.isArray(raw?.data) ? raw.data : [];
      setMachines(list);
      if (list.length > 0) setSelectedMachine(list[0].name);
    } catch { toast.error("Failed to load machines"); }
  };

  const fetchTemperatureTrend = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`https://api.hanoch.in/api/ptc/telemetry/?machine=${selectedMachine}&date=${selectedDate}`);
      const raw = res.data ?? res;
      const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.results) ? raw.results : [];
      setTempData(rows.map(item => ({ ...item, temperature: item.temperature === 0 ? null : item.temperature })));
    } catch { toast.error("Failed to load temperature trend"); }
    finally  { setLoading(false); }
  };

  const fetchAlarmList = async () => {
    try {
      const res = await axios.get("https://api.yourdomain.com/temperature-alarms");
      setAlarmList(res.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchMachines(); }, []);

  useEffect(() => {
    if (!selectedMachine || !selectedDate) return;
    fetchTemperatureTrend();
    const id = setInterval(fetchTemperatureTrend, 60_000);
    return () => clearInterval(id);
  }, [selectedMachine, selectedDate]);

  const exportToExcel = () => {
    if (!tempData.length) { toast.warning("No data to export"); return; }
    const ws  = XLSX.utils.json_to_sheet(tempData);
    const wb  = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Temperature");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), `Temperature_${selectedMachine}_${selectedDate}.xlsx`);
  };

  /* ── valid data stats ─────────────────────────────────────────── */
  const valid = tempData.map(d => d.temperature).filter(t => t != null);
  const statsData = {
    avg_temp:    valid.length ? (valid.reduce((a,b)=>a+b,0)/valid.length).toFixed(1) : "--",
    min_temp:    valid.length ? Math.min(...valid).toFixed(1) : "--",
    max_temp:    valid.length ? Math.max(...valid).toFixed(1) : "--",
    data_points: valid.length,
  };

  const STAT_CARDS_DEFAULT = [
    { id:"avg",    label:"Avg Temp",    field:"avg_temp",    icon:"mdi mdi-thermometer",              color:"text-primary", visible:true },
    { id:"min",    label:"Min Temp",    field:"min_temp",    icon:"mdi mdi-thermometer-chevron-down", color:"text-info",    visible:true },
    { id:"max",    label:"Max Temp",    field:"max_temp",    icon:"mdi mdi-thermometer-chevron-up",   color:"text-warning", visible:true },
    { id:"points", label:"Data Points", field:"data_points", icon:"mdi mdi-chart-line",               color:"text-success", visible:true },
  ];

  const STAT_FIELDS = [
    { value:"avg_temp",    label:"Average Temp"  },
    { value:"min_temp",    label:"Min Temp"      },
    { value:"max_temp",    label:"Max Temp"      },
    { value:"data_points", label:"Data Points"   },
  ];

  const statFC = useFieldCustomizer("temp_stat_cards", STAT_CARDS_DEFAULT, []);

  const widgets = [
    {
      id: "stats",
      label: "Temperature KPIs",
      icon: "mdi mdi-thermometer",
      defaultW: 12, defaultH: 4, minH: 3,
      customizer: (
        <FieldCustomizerPanel cards={statFC.cards} columns={[]} availableFields={STAT_FIELDS}
          onCardsChange={statFC.updateCards} onColumnsChange={()=>{}} onReset={statFC.resetFields}/>
      ),
      content: (
        <div className="fc-stat-row">
          {statFC.cards.filter(c=>c.visible!==false).map(card=>(
            <div key={card.id} className="fc-stat-card">
              <div className="fc-stat-icon-wrap">
                <div className={`fc-stat-icon ${card.color}`}>
                  <i className={card.icon||"mdi mdi-thermometer"}/>
                </div>
              </div>
              <div className="fc-stat-info">
                <p className="fc-stat-label">{card.label}</p>
                <h5 className="fc-stat-value">{statsData[card.field]??'--'}</h5>
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: "chart",
      label: "Temperature Chart",
      icon: "mdi mdi-chart-line",
      defaultW: 12, defaultH: 7, minH: 5,
      content: (
        <TemperatureChart
          tempData={tempData} loading={loading}
          machines={machines}
          selectedMachine={selectedMachine} setSelectedMachine={setSelectedMachine}
          selectedDate={selectedDate}       setSelectedDate={setSelectedDate}
          onExport={exportToExcel}
        />
      ),
    },
    {
      id: "alarms",
      label: "Alarm Table",
      icon: "mdi mdi-bell-outline",
      defaultW: 12, defaultH: 6, minH: 4,
      defaultVisible: false,
      customizer: (
        <FieldCustomizerPanel cards={[]} columns={alarmFC.columns} availableFields={ALARM_COLS_DEFAULT.map(c=>({value:c.field,label:c.label}))}
          onCardsChange={()=>{}} onColumnsChange={alarmFC.updateCols} onReset={alarmFC.resetFields}/>
      ),
      content: <AlarmTable alarmList={alarmList} columnConfig={alarmFC.columns}/>,
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="OptiMize" breadcrumbItem="Temperature Trend" />
          <PageGrid pageKey="temperature_alarm" widgets={widgets} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TemperatureAlarm;
